/**
 * Utility functions for processing JSON data into HTML tables
 */

/**
 * Process response content to convert JSON to HTML tables
 * @param {string} content - The response content
 * @returns {string} - Content with JSON converted to HTML tables
 */
export const processResponseForTable = (content) => {
  try {
    // Extract JSON array from the content - find everything between first [ and last ]
    const startIndex = content.indexOf('[');
    const endIndex = content.lastIndexOf(']');
    
    if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
      // No valid JSON array found
      console.warn('No valid JSON array found in the content');
      return content;
    }
    
    // Extract the JSON array part
    const jsonString = content.substring(startIndex, endIndex + 1);
    
    try {
      // Parse the JSON string
      const jsonData = JSON.parse(jsonString);
      
      // Handle both single objects and arrays of objects
      const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      
      // If it's empty, return the original
      if (dataArray.length === 0) {
        return content;
      }
      
      // Process the test cases using the existing processNestedTestCases function
      return processNestedTestCases(dataArray);
      
    } catch (error) {
      // If JSON parsing fails, return the original content
      console.error('Error parsing JSON:', error);
      return content;
    }
  } catch (error) {
    console.error('Error processing response for table:', error);
    return content;
  }
};

/**
 * Process test cases with nested test steps
 * @param {Array} testCases - Array of test case objects
 * @returns {string} - HTML with formatted test cases
 */
const processNestedTestCases = (testCases) => {
  // Define color constants for theming
  const colors = {
    primary: '#4285f4',
    text: '#333',
    border: '#dee2e6',
    tableBorder: '#c8d6e5',
    rowEven: '#ffffff',
    rowOdd: '#f5f8fc',
    specialBg: '#f0f7ff',
    monoText: '#666',
    monoBg: '#f8f9fa',
  };

  // Define reusable style constants
  const styles = {
    baseContainer: 'all: initial; display: block; font-family: Arial, sans-serif;',
    outerContainer: `margin: 20px 0; font-family: Arial, sans-serif; border: 1px solid ${colors.border}; border-radius: 6px; overflow: hidden; box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);`,
    testCaseContainer: `margin-bottom: 20px; padding: 18px; background-color: #f8f9fa; border-left: 4px solid ${colors.primary}; border-bottom: 1px solid #e9ecef;`,
    header: `margin-top: 0; margin-bottom: 12px; color: ${colors.primary}; font-size: 16px; font-weight: 600;`,
    paragraph: `margin-bottom: 10px; line-height: 1.5; color: ${colors.text};`,
    strong: `font-weight: 600; color: ${colors.primary};`,
    table: `border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 14px; margin-top: 15px; border: 2px solid ${colors.tableBorder}; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);`,
    thead: `background-color: ${colors.primary}; color: white;`,
    th: `padding: 12px 15px; font-weight: 600; text-align: left; border: 1px solid #a2c1e8; font-size: 14px;`,
    tdBase: `padding: 10px 15px; border: 1px solid #d9e2ec; font-size: 14px; color: ${colors.text}; vertical-align: top;`,
    separator: 'border: 0; border-top: 1px dashed #ccc; margin: 30px 0;',
  };

  // Function to generate dynamic table cell styles
  const getTdStyle = (cellIndex, stepHeadersLength) => {
    let style = styles.tdBase;
    if (cellIndex === 0) {
      style += ' font-weight: 500; color: #1a73e8; width: 100px;';
    } else if (cellIndex === stepHeadersLength - 2) {
      style += ` background-color: ${colors.specialBg}; font-style: italic;`;
    } else if (cellIndex === stepHeadersLength - 1) {
      style += ` width: 100px; text-align: center; font-family: monospace; color: ${colors.monoText}; background-color: ${colors.monoBg};`;
    }
    return style;
  };

  // Function to escape HTML special characters
  const escapeHtml = (unsafe) => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  // Build the HTML
  let html = `<div style="${styles.baseContainer}">`;
  html += `<div style="${styles.outerContainer}">`;

  testCases.forEach((testCase, index) => {
    html += `<div style="${styles.testCaseContainer}">`;
    html += `<h3 style="${styles.header}">Test Case ${index + 1}: ${escapeHtml(testCase['Test Number'] || '')}</h3>`;

    // Only include fields if they exist
    if (testCase['Test Case Name']) {
      html += `<p style="${styles.paragraph}"><strong style="${styles.strong}">Test Case Name:</strong> ${escapeHtml(testCase['Test Case Name'])}</p>`;
    }
    if (testCase['Test Description']) {
      html += `<p style="${styles.paragraph}"><strong style="${styles.strong}">Description:</strong> ${escapeHtml(testCase['Test Description'])}</p>`;
    }

    // Render table if test steps exist
    if (testCase['Test Steps'] && Array.isArray(testCase['Test Steps']) && testCase['Test Steps'].length > 0) {
      html += '<div style="overflow-x: auto; margin: 20px 0;">';
      html += `<table style="${styles.table}">`;
      const stepHeaders = Object.keys(testCase['Test Steps'][0]);

      // Table header
      html += `<thead style="${styles.thead}"><tr>`;
      stepHeaders.forEach(header => {
        const formattedHeader = header.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        html += `<th style="${styles.th}">${escapeHtml(formattedHeader)}</th>`;
      });
      html += '</tr></thead>';

      // Table body
      html += '<tbody>';
      testCase['Test Steps'].forEach((step, rowIndex) => {
        const rowBgColor = rowIndex % 2 === 0 ? colors.rowEven : colors.rowOdd;
        html += `<tr style="background-color: ${rowBgColor};">`;
        stepHeaders.forEach((header, cellIndex) => {
          const cellValue = step[header] !== undefined && step[header] !== null ? escapeHtml(String(step[header])) : '';
          const cellStyle = getTdStyle(cellIndex, stepHeaders.length);
          html += `<td style="${cellStyle}">${cellValue}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
      html += '</div>';
    } else {
      html += '<p style="color: #666; font-style: italic;">No test steps available.</p>';
    }

    html += '</div>';

    if (index < testCases.length - 1) {
      html += `<hr style="${styles.separator}">`;
    }
  });

  html += '</div></div>';
  return html;
};

/**
 * Strictly validate if a string is valid JSON
 * @param {string} str - String to check
 * @returns {boolean} - True if valid JSON
 */
const isValidJsonString = (str) => {
  try {
    // Check for trailing characters by parsing then stringifying to compare
    const parsed = JSON.parse(str);
    const normalized = JSON.stringify(parsed);
    
    // Remove all whitespace to compare structure only
    const strNoSpace = str.replace(/\s/g, '');
    const normalizedNoSpace = normalized.replace(/\s/g, '');
    
    // Check if the string contains the same JSON structure
    // This helps catch cases where there are trailing characters
    if (strNoSpace.includes(normalizedNoSpace) && strNoSpace.length > normalizedNoSpace.length) {
      console.warn('JSON string contains extra characters beyond valid JSON');
      return false;
    }
    
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Check if a string contains valid JSON
 * @param {string} str - String to check
 * @returns {boolean} - True if valid JSON
 */
export const isValidJson = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};
